import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Grid,
  TextField,

  Stack, Collapse, Autocomplete, Chip
} from '@mui/material';
import { useAppDispatch } from '../../../redux/storeExports';
import { fetchRemovalBrokers } from '../../../redux/brokerService';
import { RemovalBrokersResponse, BrokersRequestFilter, RemovalBroker } from '../../../redux/RemovalBrokersTypes/RemovalBrokersTypes';
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material"
import { formatRemovalGroup } from '../../../utils/helpers';
import ConfirgureRemovalBrokersModal from '../../shared/molecule/ConfigureRemovalBrokersModal';
import { SubscriptionTierOptions } from '../UserReports/Types';
import { API } from 'aws-amplify'
import { Toast } from '../../toast/toast';
import { fetchCognitoUsers } from '@meprism/shared/src/redux/authentication/authenticationSlice';
import {ACTIVEOPTIONS, REMOVALROLESOPTIONS, REMOVALROLEMAPPING} from '@meprism/shared/src/utils/constants'
import LottieLoader from '../../shared/atom/LottieLoader';


const BooleanOptions = [
    'True',
    'False'
]

const configureRemovalBrokerInitialState = {
    id: 0,
    name: '',
    display_name: '',
    is_scanner_enabled: 0,
    is_removal_enabled: 0,
    is_confirmator_enabled: 0,
    is_confirmator_supported: 0,
    broker_tier: '',
    removal_assignees: null,
    removal_group: null,
    is_removal_on_hold: false
}

const brokersRequestFilterInitialState = {
    is_scanner_enabled: null,
    is_removal_enabled: null,
    is_confirmator_enabled: null,
    is_confirmator_supported: null,
    is_removal_on_hold: null,
    removal_assignees:  null,
    removal_group: null,
    display_name: null,
    broker_tier: null
}

type configChangeRequest = {
    brokerName: string, brokerId: string, removalAssigneeList : string[] | null, removalGroup: string | null, is_removal_on_hold: boolean
}

export const RemovalBrokersTab=()=>{
    const dispatch = useAppDispatch()
    const [cognitoUsers, setCognitoUsers] = useState<Array<any>>([])
    const [loading, setIsLoading] = useState(true)
    const [brokers, setBrokers] = useState<RemovalBrokersResponse>([])
    const [filters, setFilters] = useState<BrokersRequestFilter>(brokersRequestFilterInitialState)
    const [openConfigureModal, setOpenConfigureModal] = useState(false)
    const [configureModalBroker, setConfigureModalBroker] = useState<RemovalBroker>(configureRemovalBrokerInitialState as RemovalBroker)
    const [expandedRow, setExpandedRow] = useState<null | number>(null)

    const fetchBrokers = useCallback(async () =>{
        setIsLoading(true)
        const response = await dispatch(fetchRemovalBrokers()).unwrap()
        setBrokers(response)
        setIsLoading(false)
     },[dispatch])

    const handleConfigChange=useCallback(async(request: configChangeRequest)=>{
        try{
            await API.patch('CorpOnboarding', `/brokers/${request.brokerName}/${request.brokerId}`, {
                body: {
                    'removal_assignees' : request.removalAssigneeList,
                    'removal_group': request.removalGroup,
                    'is_removal_on_hold': request.is_removal_on_hold,
                },
            });
            fetchBrokers()
        }catch(e){
            Toast.show(
                {
                    'type': 'success',
                    'text1': `Error setting configuration for ${request.brokerName}`
                }
            )
        }
    },[fetchBrokers])

    const handleFilterChange = (filter: string, value: any) => {
        setFilters(prev => ({
            ...prev,
            [filter]: value
        }));
    }

    const brokersOptions = useMemo(() => {
        if (!brokers || brokers.length === 0) return [];

        return brokers.map((broker) => broker.display_name);
    }, [brokers]);


    const filteredRows = useMemo(() => {
    if (!brokers || brokers.length === 0) {
        return brokers;
    }

    return brokers.filter((row) => {
        if (
            filters.display_name &&
            row.display_name !== filters.display_name
        ){
            return false
        }
        if (
            filters.is_scanner_enabled !== null &&
            row.is_scanner_enabled !== filters.is_scanner_enabled
        ) {
            return false
        }
        if (
            filters.broker_tier !== null &&
            row.broker_tier !== filters.broker_tier
        ) {
            return false
        }

        if (
            filters.is_removal_enabled !== null &&
            row.is_removal_enabled !== filters.is_removal_enabled
        ) {
            return false
        }

        if (
            filters.is_confirmator_enabled !== null &&
            row.is_confirmator_enabled !== filters.is_confirmator_enabled
        ) {
            return false
        }

        if (
            filters.is_confirmator_supported !== null &&
            row.is_confirmator_supported !== filters.is_confirmator_supported
        ) {
            return false
        }

        if (filters.removal_group && filters.removal_group.length > 0) {
            const selectedGroups = filters.removal_group;

            const isNASelected = selectedGroups.includes("N/A");

            if (row.removal_group === null) {
                if (!isNASelected) {
                    return false;
                }
            } else {
                if (!selectedGroups.includes(row.removal_group)) {
                    return false;
                }
            }
        }

        if (
                filters.removal_assignees &&
                filters.removal_assignees.length > 0
            ) {
                const selectedAssignees = filters.removal_assignees;
                const isNASelected = selectedAssignees.includes("N/A");

                const hasNoAssignees =
                    !row.removal_assignees || row.removal_assignees.length === 0;

                const hasMatchingAssignee =
                    row.removal_assignees &&
                    row.removal_assignees.some((assignee) =>
                        selectedAssignees.includes(assignee)
                    );

                if (
                    !(hasMatchingAssignee || (isNASelected && hasNoAssignees))
                ) {
                    return false;
                }
            }
        if (
            filters.is_removal_on_hold !== null &&
            row.is_removal_on_hold !== filters.is_removal_on_hold
        ) {
            return false;
        }


        return true
    })
    }, [brokers, filters]);

    const getActiveIndicator = (value : 1 | 0) =>{
        return(
            <Box
            component="span"
            sx={{
                px: 2,
                py: 0.8,
                borderRadius: '12px',
                backgroundColor:
                value === 1 ? '#e6f7ed' : '#ffeaea',
                color:
                value === 1 ? '#1e8e3e' : '#d93025',
                fontWeight: 500,
                fontSize: '0.75rem',
                textTransform: 'capitalize',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
            }}>
            <Box
                component="span"
                sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                backgroundColor:
                    value === 1 ? '#1e8e3e' : '#d93025',
                }}
            />
            {value === 1 ? 'Active' : 'Inactive'}
            </Box>
        )
    }

    const getRemovalRoleKeyFromValue = (value: string) =>
        Object.keys(REMOVALROLEMAPPING).find(
        key => REMOVALROLEMAPPING[key as keyof typeof REMOVALROLEMAPPING] === value
    )
    const assigneeOptions = useMemo(() => {
        if (!cognitoUsers || cognitoUsers.length === 0) return [];

        let options = ["N/A", ...cognitoUsers
            .filter((user) => {
                if (!filters.removal_group) return true;

                const role = user.Attributes?.["custom:removal_role"];

                if (filters.removal_group.includes("N/A") && !role) {
                    return true;
                }

                return role ? filters.removal_group.includes(role) : false;
            })
            .map((user) => user.Username)]
        return options
    }, [cognitoUsers, filters.removal_group]);


    const colStyle = {
        fontSize: '16px',
        whiteSpace: 'nowrap',
        minWidth: 'max-content',
        maxWidth: 'max-content'
    }

    const headerStyle = {
        fontWeight:'bold',
        fontSize: '16px',
        whiteSpace: 'nowrap',
        minWidth: 'max-content',
        maxWidth: 'max-content'
    }


    const getCognitoUsers = useCallback(async () => {
        const response = await dispatch(fetchCognitoUsers()).unwrap()
        setCognitoUsers(response)
    },[dispatch])
    
    useEffect(()=>{
        fetchBrokers()
        getCognitoUsers()
    },[fetchBrokers, getCognitoUsers])

    const openConfigureBrokersModal=useCallback((broker: RemovalBroker)=>{
        setConfigureModalBroker(broker)
        setOpenConfigureModal(true)
    },[])

    const configureButton=useCallback((broker: RemovalBroker)=>{
        return(
            <Button variant="outlined" onClick={()=>openConfigureBrokersModal(broker)}>
                Configure
            </Button>
        )
    },[openConfigureBrokersModal])

    useEffect(() => {
        if (filters.removal_group && filters.removal_assignees) {

            const updatedAssignees = filters.removal_assignees.filter(
                (assignee) => assigneeOptions.includes(assignee)
            );
            if (updatedAssignees.length !== filters.removal_assignees.length) {
                handleFilterChange(
                    "removal_assignees",
                    updatedAssignees.length ? updatedAssignees : null
                );
            }
        }
    }, [filters.removal_group, assigneeOptions, filters.removal_assignees]);

    return(
        <Box width={'100%'}>
            {loading?
                <Box
                    sx={{
                        width: '100%',
                        height: '50vh',
                        alignItems: 'center',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                    }}>
                    <LottieLoader />
                </Box>
                :<>
                    <Box sx={{display:'flex', alignItems:'center', justifyContent:'center'}}>                
                        <Grid container spacing={4} mb={10} mt={5} width={'80vw'}>
                            <Grid item lg={4} md={6} xs={12}>
                                <Autocomplete
                                    options={brokersOptions}
                                    value={filters.display_name}
                                    onChange={(event, newValue) => {
                                        handleFilterChange('display_name',newValue);
                                    }}
                                    clearOnEscape
                                    renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Broker"
                                    />
                                    )}
                                    fullWidth
                                    isOptionEqualToValue={(option, value) => option === value}
                                />
                            </Grid>
                            <Grid item lg={4} md={6} xs={12}>
                                <Autocomplete
                                    options={SubscriptionTierOptions}
                                    value={filters.broker_tier}
                                    onChange={(event, newValue) => {
                                        handleFilterChange('broker_tier',newValue);
                                    }}
                                    clearOnEscape
                                    renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Broker Tier"
                                    />
                                    )}
                                    fullWidth
                                    isOptionEqualToValue={(option, value) => option === value}
                                />
                            </Grid>
                            <Grid item lg={4} md={6} xs={12}>
                                <Autocomplete
                                    multiple
                                    options={REMOVALROLESOPTIONS}
                                    value={
                                            filters.removal_group
                                            ? filters.removal_group.map((newValue) =>
                                                    getRemovalRoleKeyFromValue(newValue)
                                                )
                                            : []
                                        }
                                    onChange={(event, newValue) => {
                                        const mappedValues = newValue.length
                                        ? newValue.map(
                                            (value) =>
                                                REMOVALROLEMAPPING[
                                                value as keyof typeof REMOVALROLEMAPPING
                                                ]
                                            )
                                        : null;          
            
                                        handleFilterChange("removal_group", mappedValues);

                                    }}
                                    clearOnEscape
                                    renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Removal Group"
                                    />
                                    )}
                                    fullWidth
                                    limitTags={2}
                                    isOptionEqualToValue={(option, value) => option === value}
                                    renderTags={(value, getTagProps) =>
                                        value.map((option, index) => (
                                        <Chip label={option} {...getTagProps({ index })} key={option} />
                                        ))
                                    }
                                />
                            </Grid>
                            <Grid item lg={4} md={6} xs={12}>
                                <Autocomplete
                                    multiple
                                    options={assigneeOptions}
                                    value={filters.removal_assignees ?? []}
                                    onChange={(event, newValue) => {
                                        handleFilterChange("removal_assignees", newValue.length ? newValue : null);
                                    }}
                                    clearOnEscape
                                    filterSelectedOptions
                                    renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Removal Assignees"
                                    />
                                    )}
                                    fullWidth
                                    limitTags={2}
                                    isOptionEqualToValue={(option, value) => option === value}
                                    renderTags={(value, getTagProps) =>
                                        value.map((option, index) => (
                                            <Chip label={option} {...getTagProps({ index })} />
                                        ))
                                    }
                                    />

                            </Grid>
                            <Grid item lg={4} md={6} xs={12}>
                                <Autocomplete
                                    options={BooleanOptions}
                                    value={
                                    filters.is_confirmator_supported === null
                                        ? null
                                        : filters.is_confirmator_supported === 1
                                        ? "True"
                                        : "False"
                                    }
                                    onChange={(event, newValue) => {
                                        if (newValue === null) {
                                            handleFilterChange("is_confirmator_supported", null);
                                        } else {
                                            handleFilterChange(
                                                "is_confirmator_supported",
                                                newValue === "True" ? 1 : 0
                                            );
                                        }
                                    }}
                                    clearOnEscape
                                    renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Confirmator Supported"
                                    />
                                    )}
                                    fullWidth
                                    isOptionEqualToValue={(option, value) => option === value}
                                    />
                            </Grid>
                            <Grid item lg={4} md={6} xs={12}>
                                <Autocomplete
                                    options={ACTIVEOPTIONS}
                                    value={
                                    filters.is_scanner_enabled === null
                                        ? null
                                        : filters.is_scanner_enabled === 1
                                        ? "Active"
                                        : "Inactive"
                                    }
                                    onChange={(event, newValue) => {
                                        if (newValue === null) {
                                            handleFilterChange("is_scanner_enabled", null);
                                        } else {
                                            handleFilterChange(
                                                "is_scanner_enabled",
                                                newValue === "Active" ? 1 : 0
                                            );
                                        }
                                    }}
                                    clearOnEscape
                                    renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Scanner Active"
                                    />
                                    )}
                                    fullWidth
                                    isOptionEqualToValue={(option, value) => option === value}
                                    />
                            </Grid>
                            <Grid item lg={4} md={6} xs={12}>
                                <Autocomplete
                                    options={ACTIVEOPTIONS}
                                    value={
                                    filters.is_removal_enabled === null
                                        ? null
                                        : filters.is_removal_enabled === 1
                                        ? "Active"
                                        : "Inactive"
                                    }
                                    onChange={(event, newValue) => {
                                        if (newValue === null) {
                                            handleFilterChange("is_removal_enabled", null);
                                        } else {
                                            handleFilterChange(
                                                "is_removal_enabled",
                                                newValue === "Active" ? 1 : 0
                                            );
                                        }
                                    }}
                                    clearOnEscape
                                    renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Removal Active"
                                    />
                                    )}
                                    fullWidth
                                    isOptionEqualToValue={(option, value) => option === value}
                                    />
                            </Grid>
                            <Grid item lg={4} md={6} xs={12}>
                                <Autocomplete
                                    options={ACTIVEOPTIONS}
                                    value={
                                    filters.is_confirmator_enabled === null
                                        ? null
                                        : filters.is_confirmator_enabled === 1
                                        ? "Active"
                                        : "Inactive"
                                    }
                                    onChange={(event, newValue) => {
                                        if (newValue === null) {
                                            handleFilterChange("is_confirmator_enabled", null);
                                        } else {
                                            handleFilterChange(
                                                "is_confirmator_enabled",
                                                newValue === "Active" ? 1 : 0
                                            );
                                        }
                                    }}
                                    clearOnEscape
                                    renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Confirmator Active"
                                    />
                                    )}
                                    fullWidth
                                    isOptionEqualToValue={(option, value) => option === value}
                                    />
                            </Grid>
                            <Grid item lg={4} md={6} xs={12}>
                                <Autocomplete
                                    options={BooleanOptions}
                                    value={
                                    filters.is_removal_on_hold === null
                                        ? null
                                        : filters.is_removal_on_hold 
                                        ? "True"
                                        : "False"
                                    }
                                    onChange={(event, newValue) => {
                                        if (newValue === null) {
                                            handleFilterChange("is_removal_on_hold", null);
                                        } else {
                                            handleFilterChange(
                                                "is_removal_on_hold",
                                                newValue === "True"
                                            );
                                        }
                                    }}
                                    clearOnEscape
                                    renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Removal On Hold"
                                    />
                                    )}
                                    fullWidth
                                    isOptionEqualToValue={(option, value) => option === value}
                                    />
                            </Grid>
                            <Grid item lg={4} md={6} xs={12}>
                                <Button
                                    variant='outlined'
                                    sx={{height:'100%'}}
                                    onClick={()=>setFilters(brokersRequestFilterInitialState)}
                                >
                                    Clear Filters
                                </Button>
                            </Grid>
                        </Grid>
                    </Box>
                    <TableContainer component={Paper} style={{ marginTop: 20 }}>
                        <Table sx={{minWidth: 1580, overflow: 'auto' }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell></TableCell>
                                    <TableCell sx={{...headerStyle}}>Broker Name</TableCell>
                                    <TableCell sx={{...headerStyle}}>Broker Tier</TableCell>
                                    <TableCell sx={{...headerStyle}}>Scanner Active</TableCell>
                                    <TableCell sx={{...headerStyle}}>Removal Active</TableCell>
                                    <TableCell sx={{...headerStyle}}>Confirmator Supported</TableCell>
                                    <TableCell sx={{...headerStyle}}>Confirmator Active</TableCell>
                                    <TableCell sx={{...headerStyle}}>Removal On Hold</TableCell>
                                    <TableCell sx={{...headerStyle}}>Removal Group</TableCell>
                                    <TableCell sx={{...headerStyle}}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {Array.isArray(filteredRows) &&
                                    filteredRows.length===0 &&
                                    <TableRow>
                                        <TableCell colSpan={11} sx={{textAlign:"center"}}>
                                            No Record Found
                                        </TableCell>
                                    </TableRow>
                                }
                                {Array.isArray(filteredRows) && filteredRows.length!==0 && filteredRows
                                    .map((row: RemovalBroker, index: number) => (
                                    <>
                                    <TableRow
                                        key={index}
                                        sx={{ backgroundColor: index % 2 === 0 ? "#f9f9f9" : "#ffffff"}}
                                    >
                                        <TableCell>
                                            {Boolean(row.removal_assignees) &&
                                            <Button onClick={()=>expandedRow===row.id?setExpandedRow(null):setExpandedRow(row.id)}>
                                                {expandedRow===row.id?
                                                    <KeyboardArrowUp/> :
                                                    <KeyboardArrowDown/>
                                                }
                                            </Button>}
                                        </TableCell>
                                        <TableCell sx={colStyle}>{row.display_name}</TableCell>
                                        <TableCell sx={colStyle}>{row.broker_tier}</TableCell>
                                        <TableCell sx={colStyle}>{getActiveIndicator(row.is_scanner_enabled)}</TableCell>
                                        <TableCell sx={colStyle}>{getActiveIndicator(row.is_removal_enabled)}</TableCell>
                                        <TableCell sx={colStyle}>{row.is_confirmator_supported? 'True': 'False'}</TableCell>
                                        <TableCell sx={colStyle}>{getActiveIndicator(row.is_confirmator_enabled)}</TableCell>
                                        <TableCell sx={colStyle}>{row.is_removal_on_hold? 'True': 'False'}</TableCell>
                                        <TableCell sx={colStyle}>{row.removal_group? formatRemovalGroup(row.removal_group): ''}</TableCell>
                                        <TableCell>{configureButton(row)}</TableCell>
                                    </TableRow>
                                    {row.removal_assignees && row.removal_assignees.length !==0 &&
                                        <TableRow>
                                            <TableCell colSpan={7} sx={{ paddingBottom: 0, paddingTop: 0 }}>
                                                <Collapse
                                                in={expandedRow === row.id}
                                                timeout="auto"
                                                unmountOnExit
                                                >
                                                    <Stack
                                                        spacing={2}
                                                        px={2}
                                                        py={4}
                                                        direction="row"
                                                        sx={{     
                                                            rowGap: 2,                
                                                            columnGap: 2,             
                                                            minHeight: '120px',
                                                            height: 'auto',
                                                            maxHeight: 'max-content'
                                                        }}
                                                        >
                                                        <Typography
                                                            variant="h3"
                                                            alignSelf="center"
                                                            maxWidth={100}
                                                            minWidth={100}
                                                        >
                                                            Broker Assignees
                                                        </Typography>
                                                        <Stack
                                                            spacing={0}
                                                            px={2}
                                                            py={4}
                                                            direction="row"
                                                            sx={{
                                                                flexWrap: 'wrap',         
                                                                rowGap: 2,                
                                                                columnGap: 4,             
                                                                minHeight: '120px',
                                                                height: 'auto',
                                                                maxHeight: 'max-content',
                                                                alignItems:'center'
                                                            }}
                                                        >
                                                            {row.removal_assignees.map((child: string, index: number) => (
                                                                <Paper
                                                                key={index}
                                                                sx={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    padding: '10px',
                                                                    ...colStyle,
                                                                    minWidth: 190,
                                                                    height:60
                                                                }}
                                                                >
                                                                    <Typography variant="body2" fontWeight="bold">
                                                                        {index + 1}. {child}
                                                                    </Typography>
                                                                </Paper>
                                                            ))}
                                                        </Stack>
                                                        </Stack>

                                            </Collapse>
                                        </TableCell>
                                    </TableRow>}
                                    </>
                                    ))
                                }
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <ConfirgureRemovalBrokersModal
                        open={openConfigureModal}
                        setOpen={setOpenConfigureModal}
                        broker={configureModalBroker}
                        users={cognitoUsers}
                        configUpdate={handleConfigChange}
                    />
                </>
            }
        </Box>
    )
}